// src/components/rpa/agent/EngagementSetupCard.js
import React, { useState, useRef } from 'react';
import axios from 'axios';
import AnomalyEscalationCard from './AnomalyEscalationCard';
import './EngagementSetupCard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const AUDIT_TYPES = [
  { id: 'financial_statement', label: 'Financial Statement Audit', templateId: 'financial_statement_audit_v1', classification: 'financial_statement' },
  { id: 'soc_cuec',            label: 'SOC/CUEC Controls Audit',   templateId: 'soc_cuec_v1',                classification: 'soc_cuec'            },
  { id: 'wsfr_funds',          label: 'FWS WSFR Funds Compliance', templateId: 'fws_wsfr_v1',                classification: 'wsfr_funds'          },
];

const FILE_SLOTS = [
  { key: 'primary',                        label: 'Balance Sheet / Financial Statement', required: true,  endpoint: null,                                                    fieldName: 'file',                   accept: '.pdf'        },
  { key: 'upload_trial_balance',            label: 'Trial Balance',                       required: true,  endpoint: '/admin/rpa/docs/{docId}/trial-balance/upload',           fieldName: 'trialBalance',           accept: '.csv,.xlsx'  },
  { key: 'upload_income_statement',         label: 'Income Statement',                    required: true,  endpoint: '/admin/rpa/docs/{docId}/income-statement/upload',         fieldName: 'incomeStatement',        accept: '.csv,.xlsx'  },
  { key: 'upload_bank_statements',          label: 'Bank Statements',                     required: true,  endpoint: '/admin/rpa/docs/{docId}/bank-statements/upload',          fieldName: 'bankStatements',         accept: '.pdf,.csv'   },
  { key: 'upload_bank_reconciliation',      label: 'Bank Reconciliation',                 required: true,  endpoint: '/admin/rpa/docs/{docId}/bank-reconciliation/upload',      fieldName: 'reconciliation',         accept: '.csv,.xlsx'  },
  { key: 'upload_bank_confirmations',       label: 'Bank Confirmations',                  required: false, endpoint: '/admin/rpa/docs/{docId}/bank-confirmations/upload',       fieldName: 'bankConfirmation',       accept: '.csv'        },
  { key: 'upload_ar_confirmations',         label: 'AR Confirmations',                    required: false, endpoint: '/admin/rpa/docs/{docId}/ar-confirmations/upload',         fieldName: 'arConfirmation',         accept: '.csv'        },
  { key: 'upload_ap_confirmations',         label: 'AP Confirmations',                    required: false, endpoint: '/admin/rpa/docs/{docId}/ap-confirmations/upload',         fieldName: 'apConfirmation',         accept: '.csv'        },
  { key: 'upload_inventory_confirmations',  label: 'Inventory Confirmations',             required: false, endpoint: '/admin/rpa/docs/{docId}/inventory-confirmations/upload',  fieldName: 'inventoryConfirmation',  accept: '.csv'        },
  { key: 'upload_inventory_observation',    label: 'Physical Inventory Observation',      required: false, endpoint: '/admin/rpa/docs/{docId}/inventory-observation/upload',    fieldName: 'inventoryObservation',   accept: '.csv'        },
];

// Full AI chain — all steps in order
// endpoint: exact path from admin.js
// confirmField: exact field the controller saves (from source inspection)
// confirmField2: backup field to check
const FULL_AI_CHAIN = [
  {
    stepId:        'analyze_financial_statement',
    label:         'Analyze Financial Statement',
    endpoint:      '/admin/rpa/docs/{docId}/analyze/financial-statement',
    confirmField:  'lastFinancialAnalysisAt',          // saved by analyzeFinancialStatementController
    confirmField2: 'financialStatementAnalysis',
  },
  {
    stepId:        'extract_balances',
    label:         'Extract Balances',
    endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/extract-balances',
    confirmField:  'lastBalancesExtractedAt',           // QUEUED — saved async by extractBalancesController
    confirmField2: null,                                // nested field not always in list response
    isQueued:      true,
  },
  {
    stepId:        'assess_materiality',
    label:         'Assess Materiality',
    endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/assess-materiality',
    confirmField:  'lastMaterialityAssessedAt',
    confirmField2: null,
  },
  {
    stepId:        'test_assertions',
    label:         'Test Assertions',
    endpoint:      '/admin/rpa/docs/{docId}/test/assertions',
    confirmField:  'lastAssertionsTestedAt',            // QUEUED
    confirmField2: null,
    isQueued:      true,
  },
  {
    stepId:        'conduct_substantive_testing',
    label:         'Conduct Substantive Testing',
    endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/conduct-substantive-testing',
    confirmField:  'lastSubstantiveTestingAt',          // QUEUED
    confirmField2: null,
    isQueued:      true,
  },
  {
    stepId:        'formulate_audit_procedures',
    label:         'Formulate Audit Procedures',
    endpoint:      '/admin/rpa/docs/{docId}/formulate/audit-procedures',
    confirmField:  'lastAuditProceduresFormulatedAt',
    confirmField2: 'auditProcedures',
  },
  {
    stepId:        'conduct_audit_testing',
    label:         'Conduct Audit Testing',
    endpoint:      '/admin/rpa/docs/{docId}/conduct/financial-audit',
    confirmField:  'lastFinancialAuditTestingAt',
    confirmField2: null,
    isQueued:      true,
  },
  {
    stepId:        'generate_findings',
    label:         'Generate Audit Findings',
    endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/generate-findings',
    confirmField:  'lastFindingsGeneratedAt',
    confirmField2: null,
  },
];

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const EngagementSetupCard = ({ onComplete, onCancel }) => {
  const [step,             setStep]             = useState(1);
  const [auditType,        setAuditType]        = useState(AUDIT_TYPES[0]);
  const [clientName,       setClientName]       = useState('');
  const [engagementName,   setEngagementName]   = useState('');
  const [fiscalYearEnd,    setFiscalYearEnd]    = useState('');
  const [files,            setFiles]            = useState({});
  const [progress,         setProgress]         = useState([]);
  const [error,            setError]            = useState('');
  const [done,             setDone]             = useState(false);
  const [createdDocId,     setCreatedDocId]     = useState(null);
  const fileRefs = useRef({});

  const handleNextStep = () => {
    if (!clientName.trim())     { setError('Client name is required.'); return; }
    if (!engagementName.trim()) { setError('Engagement name is required.'); return; }
    if (!fiscalYearEnd.trim())  { setError('Fiscal year end is required.'); return; }
    setError(''); setStep(2);
  };

  const updateProg = (key, status, detail = '') =>
    setProgress(prev => prev.map(p => p.key === key ? { ...p, status, detail } : p));

  // Poll GET /admin/rpa/docs every 8s until our doc confirms the step completed.
  // Uses workflowAssignment.steps as primary (most reliable) and data fields as backup.
  const pollStepComplete = async (docId, aiStep) => {
    const { stepId, isQueued } = aiStep;
    if (isQueued) await sleep(12000); // queued steps need Azure time
    else          await sleep(4000);  // sync steps: small grace for DB commit

    const maxWait = 300_000;
    const interval = 4_000;
    const start = Date.now();

    // Data field checks per step — mirrors RpaDashboard exactly
    const dataCheck = (doc) => {
      const bsa = doc?.balanceSheetAudit || {};
      switch (stepId) {
        case 'analyze_financial_statement': return !!(doc.financialStatementAnalysis || bsa.financialStatementAnalysis || doc.lastFinancialAnalysisAt);
        case 'extract_balances':            return !!(bsa.extractedBalances || doc.extractedBalances || bsa.lastBalanceExtractionAt);
        case 'assess_materiality':          return !!(bsa.materialityAssessment || doc.materialityAssessment || doc.lastMaterialityAssessedAt);
        case 'test_assertions':             return !!(bsa.assertionTests || doc.assertionTests || doc.lastAssertionsTestedAt);
        case 'conduct_substantive_testing': return !!(bsa.substantiveTestResults || doc.lastSubstantiveTestingAt);
        case 'formulate_audit_procedures':  return !!(doc.auditProcedures || doc.lastAuditProceduresFormulatedAt);
        case 'conduct_audit_testing':       return !!(bsa.financialAuditResults || doc.financialAuditResults || doc.lastFinancialAuditTestingAt);
        case 'generate_findings':           return !!(bsa.finalFindings || doc.finalFindings || doc.lastFindingsGeneratedAt);
        default:                            return false;
      }
    };

    while (Date.now() - start < maxWait) {
      try {
        const res = await axios.get(`${API_URL}/admin/rpa/docs`, {
          withCredentials: true,
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        });
        // Handle different response shapes from listDocsController
        const raw  = res.data;
        // Mirror exact parsing from RpaDashboard.js line 3993
        const docs = raw?.items || raw?.docs || raw?.data || (Array.isArray(raw) ? raw : []);
        const doc  = docs.find(d => String(d._id) === String(docId) || String(d.id) === String(docId));

        if (doc) {
          // Primary: workflowAssignment.steps (set by each controller after saving)
          const wfStep = doc?.workflowAssignment?.steps?.find(s => s.id === stepId);
          if (wfStep?.status === 'completed') return true;
          // Secondary: actual data fields
          if (dataCheck(doc)) return true;
        }
      } catch (_) { /* keep polling */ }
      await sleep(interval);
    }
    return false;
  };

  const handleRun = async () => {
    const requiredMissing = FILE_SLOTS.filter(s => s.required && !files[s.key]);
    if (requiredMissing.length) { setError(`Required: ${requiredMissing.map(s => s.label).join(', ')}`); return; }
    setError(''); setStep(3);

    const uploadSlots = FILE_SLOTS.filter(s => files[s.key]);
    const allSteps = [
      { key: 'project',        label: 'Create project'              },
      { key: 'upload_primary', label: 'Upload & classify document'  },
      ...uploadSlots.filter(s => s.key !== 'primary').map(s => ({ key: s.key, label: `Upload ${s.label}` })),
      ...FULL_AI_CHAIN.map(s => ({ key: s.stepId, label: s.label })),
    ];
    setProgress(allSteps.map(s => ({ ...s, status: 'pending', detail: '' })));

    try {
      // 1 — Create project
      updateProg('project', 'running');
      const projRes = await axios.post(
        `${API_URL}/admin/rpa/projects`,
        { name: engagementName.trim(), client: clientName.trim(), description: `${auditType.label} — FY ${fiscalYearEnd}` },
        { withCredentials: true }
      );
      const projectId = projRes.data?.project?._id || projRes.data?._id;
      if (!projectId) throw new Error('Project creation did not return an ID');
      updateProg('project', 'done');

      // 2 — Upload primary doc + classify + assign workflow
      updateProg('upload_primary', 'running');
      const fd = new FormData();
      fd.append('file', files['primary']);
      fd.append('projectId', projectId);
      fd.append('kind', 'other');
      const uploadRes = await axios.post(`${API_URL}/admin/rpa/docs/upload`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const docId = uploadRes.data?.doc?._id || uploadRes.data?._id;
      if (!docId) throw new Error('Document upload did not return an ID');
      setCreatedDocId(docId);

      await axios.post(`${API_URL}/admin/rpa/compliance/classify`,
        { docId, classification: auditType.classification }, { withCredentials: true });
      await axios.post(`${API_URL}/admin/rpa/workflow/assign`,
        { docId, projectId, workflow: auditType.templateId }, { withCredentials: true });
      updateProg('upload_primary', 'done');

      // 3 — Upload all supporting docs
      for (const slot of uploadSlots.filter(s => s.key !== 'primary')) {
        updateProg(slot.key, 'running');
        try {
          const sfd = new FormData();
          sfd.append(slot.fieldName, files[slot.key]);
          sfd.append('docId', docId);
          const ep = slot.endpoint.replace('{docId}', docId);
          await axios.post(`${API_URL}${ep}`, sfd,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 });
          updateProg(slot.key, 'done');
        } catch (err) {
          updateProg(slot.key, 'error', err.response?.data?.error || err.message);
        }
      }

      // 4 — Full AI chain with polling and anomaly extraction
      for (const aiStep of FULL_AI_CHAIN) {
        updateProg(aiStep.stepId, 'running');
        try {
          const ep = aiStep.endpoint.replace('{docId}', docId);
          await axios.post(`${API_URL}${ep}`, { docId, projectId },
            { withCredentials: true, timeout: 180_000 });

          const confirmed = await pollStepComplete(docId, aiStep);
          updateProg(aiStep.stepId,
            confirmed ? 'done' : 'error',
            confirmed ? '' : 'Timed out — step may still processing. Check dashboard.'
          );

          // Extract anomalies after step confirms done
          if (confirmed) {
            try {
              const aRes = await axios.post(
                `${API_URL}/admin/rpa/agent/extract-anomalies`,
                { docId, stepId: aiStep.stepId },
                { withCredentials: true }
              );
              if (aRes.data?.anomalies?.length > 0) {
                setProgress(prev => prev.map(p =>
                  p.key === aiStep.stepId ? { ...p, anomalies: aRes.data.anomalies } : p
                ));
              }
            } catch (_) { /* anomaly extraction is non-fatal */ }
          }

        } catch (err) {
          if (err instanceof SyntaxError || err.message?.includes('JSON') || err.message?.includes('Unterminated')) {
            updateProg(aiStep.stepId, 'error', 'Azure response truncated — step may have partially completed. Continuing chain.');
          } else {
            const errBody = err.response?.data;
            updateProg(aiStep.stepId, 'error',
              errBody?.error || errBody?.message || `HTTP ${err.response?.status}: ${err.message}`);
          }
          await sleep(3000);
        }
      }

      setDone(true);
      if (onComplete) onComplete({ docId, projectId, docName: files['primary'].name, clientName: clientName.trim(), templateId: auditType.templateId });

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Setup failed.');
    }
  };

  const icon = (status) => {
    if (status === 'done')    return <span className="es-si done">✅</span>;
    if (status === 'running') return <span className="es-si running">⏳</span>;
    if (status === 'error')   return <span className="es-si error">❌</span>;
    return <span className="es-si pending">⏸</span>;
  };

  const requiredSlots = FILE_SLOTS.filter(s => s.required);
  const optionalSlots = FILE_SLOTS.filter(s => !s.required);
  const requiredMissing = requiredSlots.filter(s => !files[s.key]);

  return (
    <div className="es-card">
      <div className="es-header">
        <span className="es-main-icon">🗂️</span>
        <div>
          <div className="es-title">New Audit Engagement</div>
          <div className="es-sub">
            {step === 1 && 'Step 1 of 2 — Engagement details'}
            {step === 2 && 'Step 2 of 2 — Upload all documents'}
            {step === 3 && (done ? '✅ Complete' : 'Running full audit chain…')}
          </div>
        </div>
        {step < 3 && onCancel && <button className="es-x" onClick={onCancel}>✕</button>}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="es-body">
          <div className="es-label">Audit Type</div>
          <div className="es-types">
            {AUDIT_TYPES.map(t => (
              <div key={t.id} className={`es-type ${auditType.id === t.id ? 'sel' : ''}`} onClick={() => setAuditType(t)}>
                <div className="es-type-name">{t.label}</div>
              </div>
            ))}
          </div>
          <div className="es-fields">
            <div className="es-field"><label>Client Name</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Acorn Tech Inc." /></div>
            <div className="es-field"><label>Engagement Name</label>
              <input value={engagementName} onChange={e => setEngagementName(e.target.value)} placeholder="FY2024 Financial Statement Audit" /></div>
            <div className="es-field"><label>Fiscal Year End</label>
              <input value={fiscalYearEnd} onChange={e => setFiscalYearEnd(e.target.value)} placeholder="December 31, 2024" /></div>
          </div>
          {error && <div className="es-error">{error}</div>}
          <button className="es-btn primary" onClick={handleNextStep}>Next →</button>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="es-body">
          <div className="es-label">Required Documents</div>
          {requiredSlots.map(slot => (
            <div key={slot.key} className={`es-file-row ${files[slot.key] ? 'has-file' : ''}`}>
              <div className="es-file-label">{slot.label} <span className="es-req">*</span></div>
              <div className="es-file-pick" onClick={() => fileRefs.current[slot.key]?.click()}>
                {files[slot.key]
                  ? <span className="es-fname">📄 {files[slot.key].name}</span>
                  : <span className="es-fph">Choose file ({slot.accept})</span>}
              </div>
              <input type="file" accept={slot.accept} ref={el => fileRefs.current[slot.key] = el}
                style={{ display: 'none' }} onChange={e => setFiles(prev => ({ ...prev, [slot.key]: e.target.files?.[0] }))} />
            </div>
          ))}
          <div className="es-label" style={{ marginTop: 10 }}>Optional Documents</div>
          {optionalSlots.map(slot => (
            <div key={slot.key} className={`es-file-row optional ${files[slot.key] ? 'has-file' : ''}`}>
              <div className="es-file-label">{slot.label}</div>
              <div className="es-file-pick" onClick={() => fileRefs.current[slot.key]?.click()}>
                {files[slot.key]
                  ? <span className="es-fname">📄 {files[slot.key].name}</span>
                  : <span className="es-fph">Choose file ({slot.accept})</span>}
              </div>
              <input type="file" accept={slot.accept} ref={el => fileRefs.current[slot.key] = el}
                style={{ display: 'none' }} onChange={e => setFiles(prev => ({ ...prev, [slot.key]: e.target.files?.[0] }))} />
            </div>
          ))}
          <div className="es-chain-preview">
            <div className="es-cp-label">After uploads I will run the full AI chain:</div>
            {FULL_AI_CHAIN.map(s => <div key={s.stepId} className="es-cp-item">→ {s.label}{s.isQueued ? ' (async)' : ''}</div>)}
          </div>
          {error && <div className="es-error">{error}</div>}
          <div className="es-btn-row">
            <button className="es-btn secondary" onClick={() => { setStep(1); setError(''); }}>← Back</button>
            <button className="es-btn primary" onClick={handleRun} disabled={requiredMissing.length > 0}>
              {requiredMissing.length > 0 ? `Missing ${requiredMissing.length} required` : '🚀 Run Full Audit'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Progress */}
      {step === 3 && (
        <div className="es-body">
          <div className="es-progress">
            {progress.map(s => (
              <div key={s.key}>
                <div className={`es-pi ${s.status}`}>
                  {icon(s.status)}
                  <div className="es-pi-label">{s.label}</div>
                  {s.detail && <div className="es-pi-err">{s.detail}</div>}
                </div>
                {s.anomalies?.length > 0 && createdDocId && (
                  <div style={{ marginLeft: 28, marginTop: 4 }}>
                    <AnomalyEscalationCard
                      docId={createdDocId}
                      stepId={s.key}
                      stepLabel={s.label}
                      anomalies={s.anomalies}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          {error && <div className="es-error">{error}</div>}
          {done && <div className="es-done">🎉 Full audit complete — ask me to draft the management letter.</div>}
        </div>
      )}
    </div>
  );
};

export default EngagementSetupCard;








// // src/components/rpa/agent/EngagementSetupCard.js
// import React, { useState, useRef } from 'react';
// import axios from 'axios';
// import AnomalyEscalationCard from './AnomalyEscalationCard';
// import './EngagementSetupCard.css';

// const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// const AUDIT_TYPES = [
//   { id: 'financial_statement', label: 'Financial Statement Audit', templateId: 'financial_statement_audit_v1', classification: 'financial_statement' },
//   { id: 'soc_cuec',            label: 'SOC/CUEC Controls Audit',   templateId: 'soc_cuec_v1',                classification: 'soc_cuec'            },
//   { id: 'wsfr_funds',          label: 'FWS WSFR Funds Compliance', templateId: 'fws_wsfr_v1',                classification: 'wsfr_funds'          },
// ];

// const FILE_SLOTS = [
//   { key: 'primary',                        label: 'Balance Sheet / Financial Statement', required: true,  endpoint: null,                                                    fieldName: 'file',                   accept: '.pdf'        },
//   { key: 'upload_trial_balance',            label: 'Trial Balance',                       required: true,  endpoint: '/admin/rpa/docs/{docId}/trial-balance/upload',           fieldName: 'trialBalance',           accept: '.csv,.xlsx'  },
//   { key: 'upload_income_statement',         label: 'Income Statement',                    required: true,  endpoint: '/admin/rpa/docs/{docId}/income-statement/upload',         fieldName: 'incomeStatement',        accept: '.csv,.xlsx'  },
//   { key: 'upload_bank_statements',          label: 'Bank Statements',                     required: true,  endpoint: '/admin/rpa/docs/{docId}/bank-statements/upload',          fieldName: 'bankStatements',         accept: '.pdf,.csv'   },
//   { key: 'upload_bank_reconciliation',      label: 'Bank Reconciliation',                 required: true,  endpoint: '/admin/rpa/docs/{docId}/bank-reconciliation/upload',      fieldName: 'reconciliation',         accept: '.csv,.xlsx'  },
//   { key: 'upload_bank_confirmations',       label: 'Bank Confirmations',                  required: false, endpoint: '/admin/rpa/docs/{docId}/bank-confirmations/upload',       fieldName: 'bankConfirmation',       accept: '.csv'        },
//   { key: 'upload_ar_confirmations',         label: 'AR Confirmations',                    required: false, endpoint: '/admin/rpa/docs/{docId}/ar-confirmations/upload',         fieldName: 'arConfirmation',         accept: '.csv'        },
//   { key: 'upload_ap_confirmations',         label: 'AP Confirmations',                    required: false, endpoint: '/admin/rpa/docs/{docId}/ap-confirmations/upload',         fieldName: 'apConfirmation',         accept: '.csv'        },
//   { key: 'upload_inventory_confirmations',  label: 'Inventory Confirmations',             required: false, endpoint: '/admin/rpa/docs/{docId}/inventory-confirmations/upload',  fieldName: 'inventoryConfirmation',  accept: '.csv'        },
//   { key: 'upload_inventory_observation',    label: 'Physical Inventory Observation',      required: false, endpoint: '/admin/rpa/docs/{docId}/inventory-observation/upload',    fieldName: 'inventoryObservation',   accept: '.csv'        },
// ];

// // Full AI chain — all steps in order
// // endpoint: exact path from admin.js
// // confirmField: exact field the controller saves (from source inspection)
// // confirmField2: backup field to check
// const FULL_AI_CHAIN = [
//   {
//     stepId:        'analyze_financial_statement',
//     label:         'Analyze Financial Statement',
//     endpoint:      '/admin/rpa/docs/{docId}/analyze/financial-statement',
//     confirmField:  'lastFinancialAnalysisAt',          // saved by analyzeFinancialStatementController
//     confirmField2: 'financialStatementAnalysis',
//   },
//   {
//     stepId:        'extract_balances',
//     label:         'Extract Balances',
//     endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/extract-balances',
//     confirmField:  'lastBalancesExtractedAt',           // QUEUED — saved async by extractBalancesController
//     confirmField2: null,                                // nested field not always in list response
//     isQueued:      true,
//   },
//   {
//     stepId:        'assess_materiality',
//     label:         'Assess Materiality',
//     endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/assess-materiality',
//     confirmField:  'lastMaterialityAssessedAt',
//     confirmField2: null,
//   },
//   {
//     stepId:        'test_assertions',
//     label:         'Test Assertions',
//     endpoint:      '/admin/rpa/docs/{docId}/test/assertions',
//     confirmField:  'lastAssertionsTestedAt',            // QUEUED
//     confirmField2: null,
//     isQueued:      true,
//   },
//   {
//     stepId:        'conduct_substantive_testing',
//     label:         'Conduct Substantive Testing',
//     endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/conduct-substantive-testing',
//     confirmField:  'lastSubstantiveTestingAt',          // QUEUED
//     confirmField2: null,
//     isQueued:      true,
//   },
//   {
//     stepId:        'formulate_audit_procedures',
//     label:         'Formulate Audit Procedures',
//     endpoint:      '/admin/rpa/docs/{docId}/formulate/audit-procedures',
//     confirmField:  'lastAuditProceduresFormulatedAt',
//     confirmField2: 'auditProcedures',
//   },
//   {
//     stepId:        'conduct_audit_testing',
//     label:         'Conduct Audit Testing',
//     endpoint:      '/admin/rpa/docs/{docId}/conduct/financial-audit',
//     confirmField:  'lastFinancialAuditTestingAt',
//     confirmField2: null,
//     isQueued:      true,
//   },
//   {
//     stepId:        'generate_findings',
//     label:         'Generate Audit Findings',
//     endpoint:      '/admin/rpa/docs/{docId}/balance-sheet/generate-findings',
//     confirmField:  'lastFindingsGeneratedAt',
//     confirmField2: null,
//   },
// ];

// const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// const EngagementSetupCard = ({ onComplete, onCancel }) => {
//   const [step,             setStep]             = useState(1);
//   const [auditType,        setAuditType]        = useState(AUDIT_TYPES[0]);
//   const [clientName,       setClientName]       = useState('');
//   const [engagementName,   setEngagementName]   = useState('');
//   const [fiscalYearEnd,    setFiscalYearEnd]    = useState('');
//   const [files,            setFiles]            = useState({});
//   const [progress,         setProgress]         = useState([]);
//   const [error,            setError]            = useState('');
//   const [done,             setDone]             = useState(false);
//   const [createdDocId,     setCreatedDocId]     = useState(null);
//   const fileRefs = useRef({});

//   const handleNextStep = () => {
//     if (!clientName.trim())     { setError('Client name is required.'); return; }
//     if (!engagementName.trim()) { setError('Engagement name is required.'); return; }
//     if (!fiscalYearEnd.trim())  { setError('Fiscal year end is required.'); return; }
//     setError(''); setStep(2);
//   };

//   const updateProg = (key, status, detail = '') =>
//     setProgress(prev => prev.map(p => p.key === key ? { ...p, status, detail } : p));

//   // Poll GET /admin/rpa/docs every 8s until our doc confirms the step completed.
//   // Uses workflowAssignment.steps as primary (most reliable) and data fields as backup.
//   const pollStepComplete = async (docId, aiStep) => {
//     const { stepId, isQueued } = aiStep;
//     if (isQueued) await sleep(12000); // queued steps need Azure time
//     else          await sleep(4000);  // sync steps: small grace for DB commit

//     const maxWait = 300_000;
//     const interval = 4_000;
//     const start = Date.now();

//     // Data field checks per step — mirrors RpaDashboard exactly
//     const dataCheck = (doc) => {
//       const bsa = doc?.balanceSheetAudit || {};
//       switch (stepId) {
//         case 'analyze_financial_statement': return !!(doc.financialStatementAnalysis || bsa.financialStatementAnalysis || doc.lastFinancialAnalysisAt);
//         case 'extract_balances':            return !!(bsa.extractedBalances || doc.extractedBalances || bsa.lastBalanceExtractionAt);
//         case 'assess_materiality':          return !!(bsa.materialityAssessment || doc.materialityAssessment || doc.lastMaterialityAssessedAt);
//         case 'test_assertions':             return !!(bsa.assertionTests || doc.assertionTests || doc.lastAssertionsTestedAt);
//         case 'conduct_substantive_testing': return !!(bsa.substantiveTestResults || doc.lastSubstantiveTestingAt);
//         case 'formulate_audit_procedures':  return !!(doc.auditProcedures || doc.lastAuditProceduresFormulatedAt);
//         case 'conduct_audit_testing':       return !!(bsa.financialAuditResults || doc.financialAuditResults || doc.lastFinancialAuditTestingAt);
//         case 'generate_findings':           return !!(bsa.finalFindings || doc.finalFindings || doc.lastFindingsGeneratedAt);
//         default:                            return false;
//       }
//     };

//     while (Date.now() - start < maxWait) {
//       try {
//         const res = await axios.get(`${API_URL}/admin/rpa/docs`, {
//           withCredentials: true,
//           headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
//         });
//         // Handle different response shapes from listDocsController
//         const raw  = res.data;
//         // Mirror exact parsing from RpaDashboard.js line 3993
//         const docs = raw?.items || raw?.docs || raw?.data || (Array.isArray(raw) ? raw : []);
//         const doc  = docs.find(d => String(d._id) === String(docId) || String(d.id) === String(docId));

//         if (doc) {
//           // Primary: workflowAssignment.steps (set by each controller after saving)
//           const wfStep = doc?.workflowAssignment?.steps?.find(s => s.id === stepId);
//           if (wfStep?.status === 'completed') return true;
//           // Secondary: actual data fields
//           if (dataCheck(doc)) return true;
//         }
//       } catch (_) { /* keep polling */ }
//       await sleep(interval);
//     }
//     return false;
//   };

//   const handleRun = async () => {
//     const requiredMissing = FILE_SLOTS.filter(s => s.required && !files[s.key]);
//     if (requiredMissing.length) { setError(`Required: ${requiredMissing.map(s => s.label).join(', ')}`); return; }
//     setError(''); setStep(3);

//     const uploadSlots = FILE_SLOTS.filter(s => files[s.key]);
//     const allSteps = [
//       { key: 'project',        label: 'Create project'              },
//       { key: 'upload_primary', label: 'Upload & classify document'  },
//       ...uploadSlots.filter(s => s.key !== 'primary').map(s => ({ key: s.key, label: `Upload ${s.label}` })),
//       ...FULL_AI_CHAIN.map(s => ({ key: s.stepId, label: s.label })),
//     ];
//     setProgress(allSteps.map(s => ({ ...s, status: 'pending', detail: '' })));

//     try {
//       // 1 — Create project
//       updateProg('project', 'running');
//       const projRes = await axios.post(
//         `${API_URL}/admin/rpa/projects`,
//         { name: engagementName.trim(), client: clientName.trim(), description: `${auditType.label} — FY ${fiscalYearEnd}` },
//         { withCredentials: true }
//       );
//       const projectId = projRes.data?.project?._id || projRes.data?._id;
//       if (!projectId) throw new Error('Project creation did not return an ID');
//       updateProg('project', 'done');

//       // 2 — Upload primary doc + classify + assign workflow
//       updateProg('upload_primary', 'running');
//       const fd = new FormData();
//       fd.append('file', files['primary']);
//       fd.append('projectId', projectId);
//       fd.append('kind', 'other');
//       const uploadRes = await axios.post(`${API_URL}/admin/rpa/docs/upload`, fd,
//         { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
//       const docId = uploadRes.data?.doc?._id || uploadRes.data?._id;
//       if (!docId) throw new Error('Document upload did not return an ID');
//       setCreatedDocId(docId);

//       await axios.post(`${API_URL}/admin/rpa/compliance/classify`,
//         { docId, classification: auditType.classification }, { withCredentials: true });
//       await axios.post(`${API_URL}/admin/rpa/workflow/assign`,
//         { docId, projectId, workflow: auditType.templateId }, { withCredentials: true });
//       updateProg('upload_primary', 'done');

//       // 3 — Upload all supporting docs
//       for (const slot of uploadSlots.filter(s => s.key !== 'primary')) {
//         updateProg(slot.key, 'running');
//         try {
//           const sfd = new FormData();
//           sfd.append(slot.fieldName, files[slot.key]);
//           sfd.append('docId', docId);
//           const ep = slot.endpoint.replace('{docId}', docId);
//           await axios.post(`${API_URL}${ep}`, sfd,
//             { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 });
//           updateProg(slot.key, 'done');
//         } catch (err) {
//           updateProg(slot.key, 'error', err.response?.data?.error || err.message);
//         }
//       }

//       // 4 — Full AI chain with polling and anomaly extraction
//       for (const aiStep of FULL_AI_CHAIN) {
//         updateProg(aiStep.stepId, 'running');
//         try {
//           const ep = aiStep.endpoint.replace('{docId}', docId);
//           await axios.post(`${API_URL}${ep}`, { docId, projectId },
//             { withCredentials: true, timeout: 180_000 });

//           const confirmed = await pollStepComplete(docId, aiStep);
//           updateProg(aiStep.stepId,
//             confirmed ? 'done' : 'error',
//             confirmed ? '' : 'Timed out — step may still processing. Check dashboard.'
//           );

//           // Extract anomalies after step confirms done
//           if (confirmed) {
//             try {
//               const aRes = await axios.post(
//                 `${API_URL}/admin/rpa/agent/extract-anomalies`,
//                 { docId, stepId: aiStep.stepId },
//                 { withCredentials: true }
//               );
//               if (aRes.data?.anomalies?.length > 0) {
//                 setProgress(prev => prev.map(p =>
//                   p.key === aiStep.stepId ? { ...p, anomalies: aRes.data.anomalies } : p
//                 ));
//               }
//             } catch (_) { /* anomaly extraction is non-fatal */ }
//           }

//         } catch (err) {
//           if (err instanceof SyntaxError || err.message?.includes('JSON') || err.message?.includes('Unterminated')) {
//             updateProg(aiStep.stepId, 'error', 'Azure response truncated — step may have partially completed. Continuing chain.');
//           } else {
//             const errBody = err.response?.data;
//             updateProg(aiStep.stepId, 'error',
//               errBody?.error || errBody?.message || `HTTP ${err.response?.status}: ${err.message}`);
//           }
//           await sleep(3000);
//         }
//       }

//       setDone(true);
//       if (onComplete) onComplete({ docId, projectId, docName: files['primary'].name, clientName: clientName.trim(), templateId: auditType.templateId });

//     } catch (err) {
//       setError(err.response?.data?.error || err.message || 'Setup failed.');
//     }
//   };

//   const icon = (status) => {
//     if (status === 'done')    return <span className="es-si done">✅</span>;
//     if (status === 'running') return <span className="es-si running">⏳</span>;
//     if (status === 'error')   return <span className="es-si error">❌</span>;
//     return <span className="es-si pending">⏸</span>;
//   };

//   const requiredSlots = FILE_SLOTS.filter(s => s.required);
//   const optionalSlots = FILE_SLOTS.filter(s => !s.required);
//   const requiredMissing = requiredSlots.filter(s => !files[s.key]);

//   return (
//     <div className="es-card">
//       <div className="es-header">
//         <span className="es-main-icon">🗂️</span>
//         <div>
//           <div className="es-title">New Audit Engagement</div>
//           <div className="es-sub">
//             {step === 1 && 'Step 1 of 2 — Engagement details'}
//             {step === 2 && 'Step 2 of 2 — Upload all documents'}
//             {step === 3 && (done ? '✅ Complete' : 'Running full audit chain…')}
//           </div>
//         </div>
//         {step < 3 && onCancel && <button className="es-x" onClick={onCancel}>✕</button>}
//       </div>

//       {/* STEP 1 */}
//       {step === 1 && (
//         <div className="es-body">
//           <div className="es-label">Audit Type</div>
//           <div className="es-types">
//             {AUDIT_TYPES.map(t => (
//               <div key={t.id} className={`es-type ${auditType.id === t.id ? 'sel' : ''}`} onClick={() => setAuditType(t)}>
//                 <div className="es-type-name">{t.label}</div>
//               </div>
//             ))}
//           </div>
//           <div className="es-fields">
//             <div className="es-field"><label>Client Name</label>
//               <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Acorn Tech Inc." /></div>
//             <div className="es-field"><label>Engagement Name</label>
//               <input value={engagementName} onChange={e => setEngagementName(e.target.value)} placeholder="FY2024 Financial Statement Audit" /></div>
//             <div className="es-field"><label>Fiscal Year End</label>
//               <input value={fiscalYearEnd} onChange={e => setFiscalYearEnd(e.target.value)} placeholder="December 31, 2024" /></div>
//           </div>
//           {error && <div className="es-error">{error}</div>}
//           <button className="es-btn primary" onClick={handleNextStep}>Next →</button>
//         </div>
//       )}

//       {/* STEP 2 */}
//       {step === 2 && (
//         <div className="es-body">
//           <div className="es-label">Required Documents</div>
//           {requiredSlots.map(slot => (
//             <div key={slot.key} className={`es-file-row ${files[slot.key] ? 'has-file' : ''}`}>
//               <div className="es-file-label">{slot.label} <span className="es-req">*</span></div>
//               <div className="es-file-pick" onClick={() => fileRefs.current[slot.key]?.click()}>
//                 {files[slot.key]
//                   ? <span className="es-fname">📄 {files[slot.key].name}</span>
//                   : <span className="es-fph">Choose file ({slot.accept})</span>}
//               </div>
//               <input type="file" accept={slot.accept} ref={el => fileRefs.current[slot.key] = el}
//                 style={{ display: 'none' }} onChange={e => setFiles(prev => ({ ...prev, [slot.key]: e.target.files?.[0] }))} />
//             </div>
//           ))}
//           <div className="es-label" style={{ marginTop: 10 }}>Optional Documents</div>
//           {optionalSlots.map(slot => (
//             <div key={slot.key} className={`es-file-row optional ${files[slot.key] ? 'has-file' : ''}`}>
//               <div className="es-file-label">{slot.label}</div>
//               <div className="es-file-pick" onClick={() => fileRefs.current[slot.key]?.click()}>
//                 {files[slot.key]
//                   ? <span className="es-fname">📄 {files[slot.key].name}</span>
//                   : <span className="es-fph">Choose file ({slot.accept})</span>}
//               </div>
//               <input type="file" accept={slot.accept} ref={el => fileRefs.current[slot.key] = el}
//                 style={{ display: 'none' }} onChange={e => setFiles(prev => ({ ...prev, [slot.key]: e.target.files?.[0] }))} />
//             </div>
//           ))}
//           <div className="es-chain-preview">
//             <div className="es-cp-label">After uploads I will run the full AI chain:</div>
//             {FULL_AI_CHAIN.map(s => <div key={s.stepId} className="es-cp-item">→ {s.label}{s.isQueued ? ' (async)' : ''}</div>)}
//           </div>
//           {error && <div className="es-error">{error}</div>}
//           <div className="es-btn-row">
//             <button className="es-btn secondary" onClick={() => { setStep(1); setError(''); }}>← Back</button>
//             <button className="es-btn primary" onClick={handleRun} disabled={requiredMissing.length > 0}>
//               {requiredMissing.length > 0 ? `Missing ${requiredMissing.length} required` : '🚀 Run Full Audit'}
//             </button>
//           </div>
//         </div>
//       )}

//       {/* STEP 3 — Progress */}
//       {step === 3 && (
//         <div className="es-body">
//           <div className="es-progress">
//             {progress.map(s => (
//               <div key={s.key}>
//                 <div className={`es-pi ${s.status}`}>
//                   {icon(s.status)}
//                   <div className="es-pi-label">{s.label}</div>
//                   {s.detail && <div className="es-pi-err">{s.detail}</div>}
//                 </div>
//                 {s.anomalies?.length > 0 && createdDocId && (
//                   <div style={{ marginLeft: 28, marginTop: 4 }}>
//                     <AnomalyEscalationCard
//                       docId={createdDocId}
//                       stepId={s.key}
//                       stepLabel={s.label}
//                       anomalies={s.anomalies}
//                     />
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//           {error && <div className="es-error">{error}</div>}
//           {done && <div className="es-done">🎉 Full audit complete — ask me to draft the management letter.</div>}
//         </div>
//       )}
//     </div>
//   );
// };

// export default EngagementSetupCard;